import { Area, CartesianGrid, ComposedChart, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { useMediaQuery } from '@/hooks/use-media-query';
import { formatVNDShort } from '@/lib/format';
import type { ReportTrendPoint } from '@/lib/api';

const REVENUE_COLOR = 'hsl(var(--chart-1))';      // primary blue — main metric (line + gradient fill)
const UNITS_COLOR = 'hsl(var(--chart-accent))';   // warm amber — secondary line (complementary contrast)

const CHART_CONFIG = {
  units_sold: { label: 'Số lượng', color: UNITS_COLOR },
  revenue_vnd: { label: 'Doanh thu', color: REVENUE_COLOR },
} satisfies ChartConfig;

function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  return `T${parseInt(m!, 10)}/${year!.slice(2)}`;
}

export interface ReportTrendMixedProps {
  data: ReportTrendPoint[];
}

export function ReportTrendMixed({ data }: ReportTrendMixedProps) {
  // On mobile, hide the right (units) Y-axis to free horizontal space — units value still visible in tooltip.
  const isMobile = useMediaQuery('(max-width: 639px)');

  if (!data.length) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        Chưa có dữ liệu sales theo tháng.
      </div>
    );
  }

  const chartData = data.map((d) => ({
    monthLabel: formatMonthLabel(d.month),
    units_sold: d.units_sold,
    revenue_vnd: d.revenue_vnd,
  }));

  return (
    <ChartContainer config={CHART_CONFIG} className="aspect-auto h-[260px] w-full">
      <ComposedChart data={chartData} margin={{ left: 4, right: 12, top: 12, bottom: 4 }}>
        <defs>
          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={REVENUE_COLOR} stopOpacity={0.35} />
            <stop offset="95%" stopColor={REVENUE_COLOR} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="unitsGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={UNITS_COLOR} stopOpacity={0.18} />
            <stop offset="95%" stopColor={UNITS_COLOR} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="monthLabel" tickLine={false} axisLine={false} tickMargin={10} />
        <YAxis
          yAxisId="revenue"
          orientation="left"
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          width={48}
          tickFormatter={(v) => formatVNDShort(v)}
        />
        <YAxis
          yAxisId="units"
          orientation="right"
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          width={42}
          tickFormatter={(v) => v.toLocaleString('vi-VN')}
          hide={isMobile}
        />
        <ChartTooltip
          cursor={{ stroke: 'hsl(220 9% 46% / 0.4)', strokeDasharray: '3 3' }}
          content={
            <ChartTooltipContent
              labelFormatter={(label) => label as string}
              formatter={(value, name) => {
                const num = typeof value === 'number' ? value : Number(value);
                const isRevenue = name === 'revenue_vnd';
                return (
                  <div className="flex w-full items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 rounded-[2px]"
                        style={{ background: isRevenue ? REVENUE_COLOR : UNITS_COLOR }}
                      />
                      <span className="text-muted-foreground">
                        {isRevenue ? 'Doanh thu' : 'Số lượng'}
                      </span>
                    </div>
                    <span className="font-medium tabular-nums text-foreground">
                      {isRevenue ? formatVNDShort(num) : num.toLocaleString('vi-VN')}
                    </span>
                  </div>
                );
              }}
            />
          }
        />
        {/* Units — secondary metric: drawn first so revenue area sits on top */}
        <Area
          yAxisId="units"
          type="monotone"
          dataKey="units_sold"
          stroke={UNITS_COLOR}
          strokeWidth={2}
          fill="url(#unitsGradient)"
          dot={{ r: 3, fill: UNITS_COLOR, strokeWidth: 0 }}
          activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--background))' }}
        />
        {/* Revenue — main metric: thicker line with stronger gradient fill */}
        <Area
          yAxisId="revenue"
          type="monotone"
          dataKey="revenue_vnd"
          stroke={REVENUE_COLOR}
          strokeWidth={2.5}
          fill="url(#revenueGradient)"
          dot={{ r: 3.5, fill: REVENUE_COLOR, strokeWidth: 0 }}
          activeDot={{ r: 6, strokeWidth: 2, stroke: 'hsl(var(--background))' }}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
